import { useState, useEffect } from "react";
import { getReportDetailsByRetestId } from "../../api/endpoints/retests/retests";
import { getProgramTargets } from "../../api/endpoints/reports/target";
import { parseCVSSVector } from "../../../components/forms/inputs/SeverityScoreSelector";
import { deriveAssetType } from "../../api/endpoints/reports/parseReports";
import { ProgramTarget } from "../../api/endpoints/programs/parsePrograms";

export interface RetestCardData {
  retest_id: number;
  report_id: number;
  program_name: string;
  report_name: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  latest_comment?: string;
  severity?: string;
  severityCategory?: string;
  comments?: any[];
  // Additional data
  assetType?: string;
  cvssScore?: string;
  programId?: number;
  description?: string;
  programName?: string;
  asset_name?: string;
}

export const useRetestCardData = (retest: any): RetestCardData => {
  const [assetType, setAssetType] = useState<string>(retest.asset_type || "Unknown");
  const [cvssScore, setCvssScore] = useState<string>("0.0");
  const [severityCategory, setSeverityCategory] = useState<string>(retest.severityCategory || "");
  const [description, setDescription] = useState<string>(retest.description || "");
  const [programId, setProgramId] = useState<number | undefined>(retest.program_id);
  const [programName, setProgramName] = useState<string>(retest.program_name || "");
  const [assetName, setAssetName] = useState<string>(retest.asset_name || "");
  const [reportScope, setReportScope] = useState<any>(null); // To store scope for asset type derivation
  const [targetValues, setTargetValues] = useState<any>(null); // To store targetValues for asset type derivation

  useEffect(() => {
    // If we already have the main identifier (program_name and a valid asset_type), we can skip the heavy lifting
    if (retest.program_name && retest.asset_type && retest.asset_type !== "Unknown") {
      if (retest.severity) {
        try {
          const parsed = parseCVSSVector(retest.severity);
          if (parsed && parsed.score && parsed.score !== "-.-") {
            setCvssScore(parsed.score);
            if (!severityCategory) setSeverityCategory(parsed.category);
          } else {
            setCvssScore(retest.cvss_score ? retest.cvss_score.toString() : "0.0");
          }
        } catch (e) {
          setCvssScore(retest.cvss_score ? retest.cvss_score.toString() : "0.0");
        }
      }
      return;
    }

    const fetchAdditionalData = async () => {
      try {
        // Get report details to extract CVSS score and program info
        const reportResponse = await getReportDetailsByRetestId(retest.retest_id.toString());
        const reportData = reportResponse.data;

        // Extract description
        if (reportData?.description) {
          setDescription(reportData.description);
        }

        // Extract program name
        if (reportData?.program_creator?.displayName) {
          setProgramName(reportData.program_creator.displayName);
        } else if (reportData?.program_creator?.program_title) {
          setProgramName(reportData.program_creator.program_title);
        } else if (reportData?.program_title) {
          setProgramName(reportData.program_title);
        }
        
        // Extract asset name
        if (reportData?.asset_name) {
          setAssetName(reportData.asset_name);
        } else if (reportData?.assetName) {
          setAssetName(reportData.assetName);
        } else if (reportData?.program_creator?.displayName && reportData.program_creator.displayName !== "Unknown") {
          setAssetName(reportData.program_creator.displayName);
        } else if (programName && programName !== "N/A") {
          setAssetName(programName);
        }

        // Extract CVSS score and severity category
        if (reportData?.severity) {
          try {
            const parsed = parseCVSSVector(reportData.severity);
            if (parsed && parsed.score && parsed.score !== "-.-") {
              setCvssScore(parsed.score);
              setSeverityCategory(parsed.category);
            } else {
              setCvssScore(reportData.cvss_score ? reportData.cvss_score.toString() : "0.0");
            }
          } catch (parseError) {
            setCvssScore(reportData.cvss_score ? reportData.cvss_score.toString() : "0.0");
          }
        }

        // Extract program ID and scope/targetValues for asset type derivation
        if (reportData?.program_id) {
          setProgramId(reportData.program_id);
        }
        if (reportData?.scope) {
          setReportScope(reportData.scope);
        }
        if (reportData?.targetValues) {
          setTargetValues(reportData.targetValues);
        }
      } catch (error) {
        console.warn("Failed to fetch additional retest data:", error);
      }
    };

    fetchAdditionalData();
  }, [retest.retest_id, retest.program_name, retest.asset_type, retest.severity, severityCategory]);

  // Effect to derive asset type once programId, reportScope, and targetValues are available
  useEffect(() => {
    const fetchAssetCategory = async () => {
      if (programId && (reportScope || (targetValues && targetValues.length > 0))) {
        try {
          const targetsResponse = await getProgramTargets(programId);
          const targets = (targetsResponse?.targets || []) as ProgramTarget[];

          const derivedType = deriveAssetType(
            reportScope,
            targets,
            targetValues
          );

          if (derivedType && derivedType !== "N/A") {
            setAssetType(derivedType);
          }
        } catch (error) {
          console.warn("Failed to fetch program targets:", error);
        }
      }
    };
    fetchAssetCategory();
  }, [programId, reportScope, targetValues]);

  return {
    ...retest,
    assetType,
    cvssScore,
    severityCategory,
    programId,
    description,
    programName,
    asset_name: assetName
  };
};
